import { Injectable, ApplicationRef, ComponentRef, ViewContainerRef, createComponent, EnvironmentInjector } from '@angular/core';
import { ToastComponent } from './toast.component';
import { ComponentVariant } from '../types';

export interface ToastConfig {
  summary?: string;
  detail?: string;
  variant?: ComponentVariant;
  life?: number;
  position?: 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center';
  closable?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toasts: ComponentRef<ToastComponent>[] = [];
  private container?: ViewContainerRef;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {
    this.createContainer();
  }

  private createContainer(): void {
    const containerElement = document.createElement('div');
    containerElement.className = 'fixed z-[10000] pointer-events-none';
    containerElement.id = 'toast-container';
    document.body.appendChild(containerElement);
  }

  show(config: ToastConfig): void {
    const componentRef = createComponent(ToastComponent, {
      environmentInjector: this.injector
    });

    componentRef.setInput('summary', config.summary || '');
    componentRef.setInput('variant', config.variant || 'info');
    componentRef.setInput('life', config.life ?? 3000);
    componentRef.setInput('position', config.position || 'top-right');
    componentRef.setInput('closable', config.closable ?? true);

    if (config.detail) {
      componentRef.location.nativeElement.innerHTML = config.detail;
    }

    const container = document.getElementById('toast-container');
    if (container) {
      container.appendChild(componentRef.location.nativeElement);
      this.appRef.attachView(componentRef.hostView);

      componentRef.instance.closeEvent.subscribe(() => {
        this.remove(componentRef);
      });

      this.toasts.push(componentRef);
      this.updatePosition(componentRef);
    }
  }

  private updatePosition(ref: ComponentRef<ToastComponent>): void {
    const position = ref.instance.position();
    const container = document.getElementById('toast-container');
    if (container) {
      const positionMap = {
        'top-right': 'top-4 right-4',
        'top-left': 'top-4 left-4',
        'top-center': 'top-4 left-1/2 -translate-x-1/2',
        'bottom-right': 'bottom-4 right-4',
        'bottom-left': 'bottom-4 left-4',
        'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2'
      };
      container.className = `fixed z-[10000] pointer-events-none flex flex-col gap-2 ${positionMap[position]}`;
    }
  }

  private remove(ref: ComponentRef<ToastComponent>): void {
    const index = this.toasts.indexOf(ref);
    if (index > -1) {
      this.toasts.splice(index, 1);
      this.appRef.detachView(ref.hostView);
      ref.destroy();
    }
  }

  success(message: string, summary?: string): void {
    this.show({ detail: message, summary, variant: 'success' });
  }

  error(message: string, summary?: string): void {
    this.show({ detail: message, summary, variant: 'danger' });
  }

  warning(message: string, summary?: string): void {
    this.show({ detail: message, summary, variant: 'warning' });
  }

  info(message: string, summary?: string): void {
    this.show({ detail: message, summary, variant: 'info' });
  }
}

